import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import { imageUtils } from '../../utils/imageUtils';

export default function ImageUpload({ value, onChange, className = "" }) {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const file = files[0];
    
    if (!file) return;

    // Валидация файла
    const validation = imageUtils.validateImage(file);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Сжимаем изображение
      const compressedFile = await imageUtils.compressImage(file);
      
      // Создаем URL для предварительного просмотра
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;
        
        // Генерируем уникальное имя файла
        const filename = imageUtils.generateFilename(file.name);
        
        // Сохраняем изображение
        const imageUrl = imageUtils.saveImageToStorage(imageData, filename);
        
        if (imageUrl) {
          setPreview(imageData);
          onChange(imageData);
          setSuccess(`Изображение "${filename}" успешно загружено`);
        } else {
          setError('Ошибка при сохранении изображения');
        }
        
        setLoading(false);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Ошибка при обработке изображения:', error);
      setError('Ошибка при обработке изображения');
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    setError('');
    setSuccess('');
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Изображение блога
      </label>
      
      {preview ? (
        <div className="relative">
          <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={preview}
              alt="Предварительный просмотр"
              className="w-full h-full object-cover"
            />
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
              title="Удалить изображение"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Нажмите на изображение или перетащите новое для замены
          </p>
        </div>
      ) : (
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
            dragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleChange}
            className="hidden"
          />
          
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Upload className="w-6 h-6 text-gray-400" />
            </div>
            
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium text-blue-600">Нажмите для загрузки</span> или перетащите изображение
            </p>
            
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF до 5MB
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-blue-600 text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600"></div>
          <span>Обработка изображения...</span>
        </div>
      )}

      {/* Альтернативный способ - URL */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Или введите URL изображения
        </label>
        <div className="flex gap-2">
          <input
            type="url"
            value={preview}
            onChange={(e) => {
              setPreview(e.target.value);
              onChange(e.target.value);
              setError('');
              setSuccess('');
            }}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="https://example.com/image.jpg"
          />
          {preview && (
            <button
              onClick={handleRemove}
              className="px-3 py-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg transition-colors"
              title="Очистить"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
